import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Layers, Loader2, RefreshCw, Sliders, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { api, type RecursoDoFluxo } from '@/lib/api';
import { cn } from '@/lib/utils';

const GRUPOS = [
  {
    id: 'passos',
    titulo: 'Passos do assistente',
    icone: Layers,
    explicacao:
      'Cada passo é uma decisão da criação. Passo escondido não aparece no assistente: o valor padrão continua valendo, então o miolo sai completo sem depender daquela tela.',
  },
  {
    id: 'recursos',
    titulo: 'Recursos extras',
    icone: Sliders,
    explicacao:
      'Recursos além do básico. Esconder aqui tira a opção da interface — útil para publicar o produto aos poucos ou reservar recursos para planos mais caros.',
  },
] as const;

/**
 * Fluxo de criação: o administrador percorre o assistente item por item.
 *
 * A lista vem do banco (`recursos`), semeada com todos os passos e recursos.
 * Liberar mostra na hora para todo mundo; esconder tira a opção da interface.
 */
export function FluxoDoPainel() {
  const [recursos, setRecursos] = useState<RecursoDoFluxo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [trabalhando, setTrabalhando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resposta = await api.listarFluxoDoPainel();
      setRecursos(resposta.recursos);
    } catch (falha) {
      setErro(
        falha instanceof Error
          ? falha.message
          : 'Não foi possível carregar o fluxo de criação. Entre novamente com a conta de administrador.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alternar = async (recurso: RecursoDoFluxo, liberado: boolean) => {
    setTrabalhando(recurso.chave);
    setErro(null);
    // Responde na hora e corrige se o servidor recusar.
    setRecursos((atual) =>
      atual.map((item) => (item.chave === recurso.chave ? { ...item, liberado } : item)),
    );
    try {
      await api.definirRecurso(recurso.chave, liberado);
    } catch (falha) {
      setRecursos((atual) =>
        atual.map((item) => (item.chave === recurso.chave ? { ...item, liberado: !liberado } : item)),
      );
      setErro(falha instanceof Error ? falha.message : 'Não foi possível alterar este item.');
    } finally {
      setTrabalhando(null);
    }
  };

  const porGrupo = useMemo(() => {
    const mapa = new Map<string, RecursoDoFluxo[]>();
    for (const recurso of recursos) {
      mapa.set(recurso.grupo, [...(mapa.get(recurso.grupo) ?? []), recurso]);
    }
    for (const lista of mapa.values()) lista.sort((a, b) => a.ordem - b.ordem);
    return mapa;
  }, [recursos]);

  const visiveis = recursos.filter((recurso) => recurso.liberado).length;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Layers className="size-4" />
          Fluxo de criação
        </h2>
        <Badge tom="neutro">
          {visiveis} de {recursos.length} liberado(s)
        </Badge>
        <Button variant="contorno" tamanho="pequeno" className="ml-auto" onClick={carregar} disabled={carregando}>
          {carregando ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          Atualizar
        </Button>
      </div>

      <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
        Revise a criação passo a passo e decida o que o cliente pode personalizar. O que está
        <strong className="text-foreground"> liberado</strong> aparece no assistente; o que está
        <strong className="text-foreground"> escondido</strong> some da interface e o miolo é gerado com o padrão do
        sistema. A mudança vale na hora para todos os usuários.
      </p>

      {erro ? (
        <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      {carregando && recursos.length === 0 ? (
        <p className="flex items-center justify-center gap-2 rounded-lg border border-border p-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Carregando o fluxo...
        </p>
      ) : (
        GRUPOS.map((grupo) => {
          const lista = porGrupo.get(grupo.id) ?? [];
          if (lista.length === 0) return null;
          const liberados = lista.filter((item) => item.liberado).length;
          const Icone = grupo.icone;

          return (
            <div key={grupo.id} className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Icone className="size-3.5" />
                  {grupo.titulo}
                </h3>
                <Badge tom={liberados === lista.length ? 'sucesso' : 'atencao'}>
                  {liberados}/{lista.length}
                </Badge>
                <div className="ml-auto flex gap-2">
                  <Button
                    tamanho="pequeno"
                    variant="contorno"
                    disabled={liberados === lista.length || carregando}
                    onClick={() =>
                      void Promise.all(
                        lista.filter((item) => !item.liberado).map((item) => alternar(item, true)),
                      )
                    }
                  >
                    <Eye />
                    Liberar todos
                  </Button>
                  <Button
                    tamanho="pequeno"
                    variant="contorno"
                    disabled={liberados === 0 || carregando}
                    onClick={() =>
                      void Promise.all(
                        lista.filter((item) => item.liberado).map((item) => alternar(item, false)),
                      )
                    }
                  >
                    <EyeOff />
                    Esconder todos
                  </Button>
                </div>
              </div>

              <p className="text-[11px] leading-relaxed text-muted-foreground">{grupo.explicacao}</p>

              <ul className="divide-y divide-border/70 overflow-hidden rounded-md border border-border">
                {lista.map((recurso) => (
                  <li
                    key={recurso.chave}
                    className={cn(
                      'flex items-start gap-3 px-3 py-2.5',
                      recurso.liberado ? 'bg-card' : 'bg-muted/40',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            'text-xs font-medium',
                            recurso.liberado ? 'text-foreground' : 'text-muted-foreground line-through',
                          )}
                        >
                          {recurso.titulo}
                        </span>
                        {recurso.liberado ? (
                          <Badge tom="sucesso" className="text-[10px]">
                            <Eye className="size-3" />
                            no assistente
                          </Badge>
                        ) : (
                          <Badge tom="atencao" className="text-[10px]">
                            <EyeOff className="size-3" />
                            escondido
                          </Badge>
                        )}
                      </span>
                      <span className="mt-0.5 block text-[11px] leading-relaxed text-muted-foreground">
                        {recurso.descricao}
                      </span>
                      <span className="mt-0.5 block font-mono text-[10px] text-muted-foreground/70">
                        {recurso.chave}
                      </span>
                    </div>

                    <Switch
                      checked={recurso.liberado}
                      disabled={trabalhando === recurso.chave}
                      onCheckedChange={(valor) => void alternar(recurso, valor)}
                      title={recurso.liberado ? 'Esconder do assistente' : 'Liberar no assistente'}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
    </section>
  );
}
